import React, { useState } from 'react';
import { Database, Download, AlertCircle, CheckCircle, Loader, Building2 } from 'lucide-react';
import apiClient from '../services/api';

const VIOLET = '#667eea', VIOLET_DK = '#764ba2';

const getSessionData = () => {
  try {
    const s = localStorage.getItem('ltms_session');
    if (!s) return null;
    const d = JSON.parse(s);
    return d?.session || d;
  } catch { return null; }
};

const DatabaseBackupForm = () => {
  const session = getSessionData();
  const [scope, setScope] = useState('company'); // 'company' | 'company_year' | 'full'
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [lastBackup, setLastBackup] = useState(() => localStorage.getItem('ltms_last_backup') || '');

  const isSuperAdmin = !!session?.issuperadmin;

  const handleBackup = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await apiClient.get('/DatabaseBackup.php', {
        params: { scope },
        responseType: 'blob',
      });

      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.message || 'Backup failed');
      }

      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `ltms_backup_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.sql`;

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/sql' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);

      const now = new Date().toLocaleString();
      setLastBackup(now);
      localStorage.setItem('ltms_last_backup', now);
      setSuccess(`Backup downloaded: ${filename}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Backup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!session?.companyid) {
    return (
      <div className="px-6 pt-5 pb-16 box-border">
        <div className="max-w-[640px] mx-auto text-center py-16 text-gray-500">
          <Database size={40} className="mx-auto mb-3 opacity-40" />
          <h3 className="font-semibold text-gray-700 mb-1.5">No Session</h3>
          <p className="text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const scopeOptions = [
    { id: 'company', label: 'Current Company — all years', desc: `All data for ${session.companyname || 'this company'}, across every financial year.` },
    ...(isSuperAdmin ? [{ id: 'full', label: 'Entire Database (Super Admin)', desc: 'Every company, every year — the full system database.' }] : []),
  ];

  return (
    <div className="px-6 pt-5 pb-16 box-border">
      <div className="max-w-[640px] mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg flex-shrink-0"
            style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
            <Database size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 m-0">Database Backup</h1>
            <p className="text-sm text-gray-500 m-0 mt-1">Download a SQL backup scoped to your current session</p>
          </div>
        </div>

        {/* Session context strip */}
        <div className="flex gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 rounded-lg text-sm">
            <Building2 size={15} className="text-violet-500" />
            <span className="text-gray-500">Company:</span>
            <span className="font-semibold text-gray-800">{session.companyname || '—'}</span>
          </div>
        </div>

        {/* Alerts */}
        {error   && <div className="flex items-center gap-2 px-4 py-3 bg-red-100 border border-red-300 text-red-700 rounded-lg mb-4 text-sm"><AlertCircle size={17} className="flex-shrink-0"/><strong>Error:</strong> {error}</div>}
        {success && <div className="flex items-center gap-2 px-4 py-3 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-lg mb-4 text-sm"><CheckCircle size={17} className="flex-shrink-0"/><strong>Success:</strong> {success}</div>}

        {/* Scope selector */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-4">
          <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-900 mb-4 pb-3 border-b-2 border-slate-100">
            <Download size={17} style={{ color: VIOLET }} /> Select Backup Scope
          </div>

          <div className="flex flex-col gap-2.5 mb-5">
            {scopeOptions.map(opt => (
              <label key={opt.id}
                className={`flex items-start gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all duration-150 ${scope === opt.id ? 'border-violet-400 bg-violet-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <input type="radio" name="scope" value={opt.id} checked={scope === opt.id}
                  onChange={() => setScope(opt.id)} className="mt-1 accent-violet-500" />
                <div>
                  <div className="text-sm font-semibold text-gray-800">{opt.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>

          {lastBackup && (
            <div className="text-xs text-gray-400 mb-4">
              Last backup downloaded: <span className="font-medium text-gray-600">{lastBackup}</span>
            </div>
          )}

          <button
            onClick={handleBackup}
            disabled={loading}
            className="flex items-center justify-center gap-2 w-full px-5 py-3 text-white border-0 rounded-lg text-[0.95rem] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 hover:shadow-lg transition-all duration-150"
            style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}
          >
            {loading
              ? <><Loader size={18} className="animate-spin" /> Generating backup…</>
              : <><Download size={18} /> Download Backup</>}
          </button>

          <p className="text-xs text-gray-400 mt-3 m-0">
            This may take a few moments depending on data volume. Don't close this page while it's running.
          </p>
        </div>

        {/* Info note */}
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[13px]">
          <strong>Note:</strong> Shared master data (countries, currencies, payment modes, etc.) is always included
          in full since it isn't tied to a specific company. Operational data (consignments, jobs, invoices, payments)
          is filtered to your selected scope above.
        </div>

      </div>
    </div>
  );
};

export default DatabaseBackupForm;